import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ITemplate, IUpsertTemplateRequest } from "../../models/Template/Template";
import { TemplateQueryKeys } from "./keys";

export default function useTemplateMutations() {
  const { post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Template sync");

  // An empty customer list syncs every enabled Wilma customer.
  const syncTemplates = useMutation({
    mutationFn: (customerIds: string[]) => post(`/template/sync`, { customers: customerIds }),
    onSuccess: () => {
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const create = useMutation({
    mutationFn: (req: IUpsertTemplateRequest) => post("/template", req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TemplateQueryKeys.all() });
    },
  });

  const update = useMutation({
    mutationFn: (template: ITemplate) =>
      put(`/template/${template.id}`, {
        type: template.type,
        title: template.title,
        body: template.body,
      } as IUpsertTemplateRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TemplateQueryKeys.all() });
    },
  });

  const remove = useMutation({
    mutationFn: (templateId: string) => del(`/template/${templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TemplateQueryKeys.all() });
    },
  });

  return {
    syncTemplates,
    create,
    update,
    remove,
  };
}
